package pl.decks.personal.components.widgets.disclaimer

import com.varabyte.kobweb.compose.css.ColorInterpolationMethod
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.colorMix
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout

object DisclaimerBoxStylesheet {
    object Vars {
        val disclaimerAccentColor by StyleVariable<Color>()

        object Internals {
            val currentTransitionColor by StyleVariable<Color>()
        }
    }

    object Container {
        sealed interface Kind : ComponentKind

        val Base = CssStyle.base<Kind> {
            Modifier
                .padding(SiteLayout.Space.Medium)
                .borderRadius(SiteLayout.Rounding.Medium)
                .gap(SiteLayout.Space.Small)
                .border(
                    width = SiteLayout.Line.Normal,
                    style = LineStyle.Solid,
                    color = Vars.Internals.currentTransitionColor.value(),
                )
                .backgroundImage(
                    linearGradient(
                        from = Colors.Transparent,
                        to = colorMix(
                            interpolation = ColorInterpolationMethod.Hsl,
                            color1 = Vars.Internals.currentTransitionColor.value() to 30.percent,
                            color2 = Colors.Transparent to 70.percent,
                        ),
                        dir = LinearGradient.Direction.ToRight,
                    )
                )
                .transition {
                    property("border-color", "background")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(TransitionDurationVars.Fast.value())
                }
        }
        val ClosedVariant = Base.addVariant {
            base {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    colorMode.toPalette().color,
                )
            }
            hover {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    Vars.disclaimerAccentColor.value(colorMode.toPalette().color),
                )
            }
            active {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    Vars.disclaimerAccentColor.value(colorMode.toPalette().color),
                )
            }
        }
        val OpenVariant = Base.addVariant {
            base {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    Vars.disclaimerAccentColor.value(colorMode.toPalette().color),
                )
            }
            hover {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    colorMode.toPalette().color,
                )
            }
            active {
                Modifier.setVariable(
                    Vars.Internals.currentTransitionColor,
                    colorMode.toPalette().color,
                )
            }
        }
    }

    val Heading = CssStyle.base {
        Modifier
            .fillMaxWidth().gap(SiteLayout.Space.Small)
            .fontSize(FontTypography.Size.Relative.Larger)
            .color(Vars.Internals.currentTransitionColor.value(colorMode.toPalette().color))

    }
    val ControlIcon = CssStyle.base {
        Modifier
            .fontSize(FontTypography.Size.Relative.Larger)
            .flexShrink(0)
            .transition {
                property("color")
                timingFunction(TransitionTimingFunction.EaseInOut)
                duration(TransitionDurationVars.Fast.value())
            }
    }
    val Title = FontTypography.sansSerif.extendedByBase {
        Modifier.fontWeight(FontTypography.Weight.Medium)
            .transition {
                property("color")
                timingFunction(TransitionTimingFunction.EaseInOut)
                duration(TransitionDurationVars.Fast.value())
            }
    }
}
